import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Calculator, TrendingUp, Info } from 'lucide-react';
import '../styles/PriceCalculationEditor.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriceCalculationEditor = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewInputs, setPreviewInputs] = useState({
    price_category: 5,
    guests: 25,
    days: 3
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/price-calculation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      calculatePreview(response.data, previewInputs);
    } catch (error) {
      console.error('Error fetching price settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBaseCostChange = (category, value) => {
    const newSettings = {
      ...settings,
      base_costs: {
        ...settings.base_costs,
        [category.toString()]: parseFloat(value) || 0
      }
    };
    setSettings(newSettings);
    setHasChanges(true);
    calculatePreview(newSettings, previewInputs);
  };

  const handleCategoryNameChange = (category, value) => {
    setSettings({
      ...settings,
      category_names: {
        ...settings.category_names,
        [category.toString()]: value
      }
    });
    setHasChanges(true);
  };

  const handleDayMultiplierChange = (day, value) => {
    const newSettings = {
      ...settings,
      day_multipliers: {
        ...settings.day_multipliers,
        [day.toString()]: parseFloat(value) || 1.0
      }
    };
    setSettings(newSettings);
    setHasChanges(true);
    calculatePreview(newSettings, previewInputs);
  };

  const handleFinalMultiplierChange = (value) => {
    const newSettings = {
      ...settings,
      final_budget_multiplier: parseFloat(value) || 1.0
    };
    setSettings(newSettings);
    setHasChanges(true);
    calculatePreview(newSettings, previewInputs);
  };

  const calculatePreview = async (currentSettings, inputs) => {
    try {
      // Calculate locally for immediate feedback
      const catStr = inputs.price_category.toString();
      const daysStr = inputs.days.toString();
      const baseCost = parseFloat(currentSettings.base_costs[catStr]) || 400;
      const dayMultiplier = parseFloat(currentSettings.day_multipliers[daysStr]) || 1.0;
      const subtotal = baseCost * inputs.guests * inputs.days * dayMultiplier;
      const finalPrice = subtotal * currentSettings.final_budget_multiplier;

      setPreview({
        ...inputs,
        category_name: currentSettings.category_names[catStr],
        base_cost_per_person_per_day: baseCost,
        day_multiplier: dayMultiplier,
        subtotal: Math.round(subtotal * 100) / 100,
        final_budget_multiplier: currentSettings.final_budget_multiplier,
        final_price: Math.round(finalPrice * 100) / 100
      });
    } catch (error) {
      console.error('Error calculating preview:', error);
    }
  };

  const handlePreviewInputChange = (field, value) => {
    const newInputs = {
      ...previewInputs,
      [field]: parseInt(value) || 0
    };
    setPreviewInputs(newInputs);
    if (settings) {
      calculatePreview(settings, newInputs);
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      await axios.put(`${API}/admin/price-calculation`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Preiskalkulation erfolgreich gespeichert!');
      setHasChanges(false);
      await fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Fehler beim Speichern der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  if (!settings) {
    return <div className="loading">Lädt Preiseinstellungen...</div>;
  }

  return (
    <div className="price-calculation-editor">
      <div className="editor-header">
        <h2>Preiskalkulation & Budgetverwaltung</h2>
        <div className="editor-actions">
          {hasChanges && (
            <button onClick={saveSettings} className="btn-save" disabled={loading}>
              <Save size={16} />
              Änderungen speichern
            </button>
          )}
        </div>
      </div>

      <div className="info-banner">
        <Info size={20} />
        <div>
          <strong>Preiskalkulation-Formel:</strong> Endpreis = (Basis-Kosten × Gäste × Tage × Tag-Multiplikator) × Finaler Multiplikator
        </div>
      </div>

      <div className="price-content">
        {/* Left Column: Price Categories */}
        <div className="price-categories-section">
          <h3>Preiskategorien (1-10)</h3>
          <div className="categories-table">
            <table>
              <thead>
                <tr>
                  <th>Stufe</th>
                  <th>Name</th>
                  <th>Basis-Kosten (€/Person/Tag)</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(cat => (
                  <tr key={cat}>
                    <td className="category-num">{cat}</td>
                    <td>
                      <input
                        type="text"
                        value={settings.category_names[cat.toString()] || `Category ${cat}`}
                        onChange={(e) => handleCategoryNameChange(cat, e.target.value)}
                        className="name-input"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="10"
                        value={settings.base_costs[cat.toString()] || 0}
                        onChange={(e) => handleBaseCostChange(cat, e.target.value)}
                        className="cost-input"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="multipliers-section">
            <h4>Tag-Multiplikatoren</h4>
            <div className="multipliers-grid">
              {[1, 2, 3, 4].map(day => (
                <div key={day} className="multiplier-item">
                  <label>{day === 4 ? 'Tag 4+' : `Tag ${day}`}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    max="2"
                    value={settings.day_multipliers[day.toString()] || 1.0}
                    onChange={(e) => handleDayMultiplierChange(day, e.target.value)}
                    className="multiplier-input"
                  />
                </div>
              ))}
            </div>

            <div className="final-multiplier">
              <label>Finaler Budget-Multiplikator</label>
              <input
                type="number"
                step="0.01"
                min="1"
                max="3"
                value={settings.final_budget_multiplier || 1.25}
                onChange={(e) => handleFinalMultiplierChange(e.target.value)}
                className="multiplier-input"
              />
            </div>
          </div>
        </div>

        {/* Right Column: Live Preview Calculator */}
        <div className="preview-section">
          <h3>
            <Calculator size={20} />
            Live-Kalkulator
          </h3>

          <div className="preview-inputs">
            <div className="preview-input-group">
              <label>Preiskategorie</label>
              <select
                value={previewInputs.price_category}
                onChange={(e) => handlePreviewInputChange('price_category', e.target.value)}
                className="preview-select"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(cat => (
                  <option key={cat} value={cat}>
                    {cat} - {settings.category_names[cat.toString()]} ({settings.base_costs[cat.toString()]}€)
                  </option>
                ))}
              </select>
            </div>

            <div className="preview-input-group">
              <label>Anzahl Gäste</label>
              <input
                type="number"
                min="2"
                value={previewInputs.guests}
                onChange={(e) => handlePreviewInputChange('guests', e.target.value)}
                className="preview-input"
              />
            </div>

            <div className="preview-input-group">
              <label>Anzahl Tage</label>
              <input
                type="number"
                min="1"
                max="10"
                value={previewInputs.days}
                onChange={(e) => handlePreviewInputChange('days', e.target.value)}
                className="preview-input"
              />
            </div>
          </div>

          {preview && (
            <div className="preview-result">
              <div className="calculation-steps">
                <h4>Berechnungsschritte:</h4>
                <div className="step">
                  <span>Basis-Kosten:</span>
                  <strong>{preview.base_cost_per_person_per_day}€ pro Person/Tag</strong>
                </div>
                <div className="step">
                  <span>Gäste:</span>
                  <strong>{preview.guests}</strong>
                </div>
                <div className="step">
                  <span>Tage:</span>
                  <strong>{preview.days}</strong>
                </div>
                <div className="step">
                  <span>Tag-Multiplikator:</span>
                  <strong>{preview.day_multiplier}x</strong>
                </div>
                <div className="step subtotal">
                  <span>Zwischensumme:</span>
                  <strong>{preview.subtotal.toLocaleString('de-DE')}€</strong>
                </div>
                <div className="step">
                  <span>Finaler Multiplikator:</span>
                  <strong>{preview.final_budget_multiplier}x</strong>
                </div>
                <div className="step final-price">
                  <span>Endpreis:</span>
                  <strong className="price-highlight">{preview.final_price.toLocaleString('de-DE')}€</strong>
                </div>
              </div>

              <div className="price-breakdown">
                <TrendingUp size={16} />
                <span>Preis pro Person: <strong>{(preview.final_price / preview.guests).toFixed(2)}€</strong></span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceCalculationEditor;
